import { useState, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { doc, getDoc, addDoc, collection, serverTimestamp } from 'firebase/firestore';
import { Capacitor } from '@capacitor/core';
import { auth, db } from '../firebase';
import { requestNotificationPermission, setupMessageListener, showNotification } from '../utils/fcm';
import { initializePushNotifications, removePushListeners } from '../utils/fcmCapacitor';
import Chats from './Chats';
import Profile from './Profile';
import AdminPanel from './AdminPanel';
import TodoList from './TodoList';
import './Dashboard.css';

function Dashboard({ user }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chats');

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        
        if (docSnap.exists()) {
          setUserData(docSnap.data());
          
          // Initialize push notifications based on platform
          if (Capacitor.isNativePlatform()) {
            // Use Capacitor push for native Android/iOS
            console.log('🔔 Initializing Capacitor push notifications...');
            initializePushNotifications(user.uid).catch(err => {
              console.error('Capacitor push error:', err);
            });
          } else {
            // Use web FCM for browsers/PWA
            console.log('🔔 Initializing web FCM...');
            requestNotificationPermission(user.uid).catch(err => {
              console.error('FCM permission error:', err);
            });
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();

    // Cleanup on unmount
    return () => {
      if (Capacitor.isNativePlatform()) {
        removePushListeners();
      }
    };
  }, [user]);

  // Listen for foreground push notifications (web only)
  useEffect(() => {
    // Only set up web FCM listener if not on native platform
    if (!Capacitor.isNativePlatform()) {
      const unsubscribe = setupMessageListener((payload) => {
        console.log('Received foreground message:', payload);
        
        // Show notification
        showNotification(
          payload.notification?.title || 'New Message',
          {
            body: payload.notification?.body,
            data: payload.data
          }
        );
      });

      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }
  }, []);

  useEffect(() => {
    // Log user login activity
    const logActivity = async () => {
      try {
        await addDoc(collection(db, 'activityLogs'), {
          userId: user.uid,
          userName: userData?.name || user.email,
          action: 'login',
          timestamp: serverTimestamp()
        });
      } catch (error) {
        console.error('Error logging activity:', error);
      }
    };

    if (userData) {
      logActivity();
    }
  }, [user.uid, user.email, userData]);

  const handleLogout = async () => {
    try {
      // Try to log logout activity, but don't block logout if it fails
      try {
        await addDoc(collection(db, 'activityLogs'), {
          userId: user.uid,
          userName: userData?.name || user.email,
          action: 'logout',
          timestamp: serverTimestamp()
        });
      } catch (logError) {
        console.error('Error logging logout activity:', logError);
        // Continue with logout even if logging fails
      }
      
      // Always sign out regardless of logging success
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
      // Force sign out even if there's an error
      try {
        await signOut(auth);
      } catch (finalError) {
        console.error('Final sign out attempt failed:', finalError);
      }
    }
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <div className="spinner"></div>
      </div>
    );
  }

  const isAdmin = userData?.role === 'admin';

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-content">
        {activeTab === 'chats' && <Chats user={user} userData={userData} />}
        {activeTab === 'todos' && <TodoList user={user} userData={userData} />}
        {activeTab === 'admin' && isAdmin && <AdminPanel user={user} userData={userData} />}
        {activeTab === 'profile' && (
          <Profile user={user} userData={userData} onLogout={handleLogout} />
        )}
      </div>

      <nav className="bottom-nav">
        <button
          className={`nav-item ${activeTab === 'chats' ? 'active' : ''}`}
          onClick={() => setActiveTab('chats')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <span className="nav-label">Chats</span>
        </button>

        <button
          className={`nav-item ${activeTab === 'todos' ? 'active' : ''}`}
          onClick={() => setActiveTab('todos')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <span className="nav-label">Todos</span>
        </button>

        {isAdmin && (
          <button
            className={`nav-item ${activeTab === 'admin' ? 'active' : ''}`}
            onClick={() => setActiveTab('admin')}
          >
            <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span className="nav-label">Admin</span>
          </button>
        )}

        <button
          className={`nav-item ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="nav-label">Profile</span>
        </button>
      </nav>
    </div>
  );
}

export default Dashboard;
